import type { TemplateResult } from 'lit';
import type { Article, CtaType, DisplayFormat, HeadingLevel } from '../article-cards.interface';
export type { Article };
import { FeaturedRenderer } from './article-cards.featured.renderer';
import { CardsRenderer } from './article-cards.cards.renderer';
import { TextHorizRenderer, TextVertRenderer } from './article-cards.text.renderer';

/**
 * Component-level settings passed once to each renderer constructor.
 */
export interface RendererConfig {
  listId: string;
  headingLevel: HeadingLevel;
  imageSize: string;
  imagePosition: string;
  showImage: boolean;
  showDescription: boolean;
  showTags: boolean;
  ctaType: CtaType;
  manualArticlesSize: number;
  onCardClick: (article: Article) => (e: Event) => void;
  onCtaClick: (article: Article) => (e: Event) => void;
}

/**
 * Contract implemented by every display-format renderer.
 */
export interface CardRenderer {
  /** Renders a single article card. index is 0-based within the current page. total is the full page size. */
  renderCard(article: Article, index: number, total?: number): TemplateResult;

  /** Renders a single skeleton placeholder for the loading state. */
  renderSkeleton(): TemplateResult;

  /**
   * Returns how many skeleton placeholders to show while loading.
   * @param perRow - parseInt of the perRow attribute
   * @param numRows - parseInt of the numRows attribute
   */
  getSkeletonCount(perRow: number, numRows: number): number;
}

/**
 * Creates the correct renderer for the given display format.
 * isManual is expressed as format='manual', not as a prop — so the
 */
export const CardRendererFactory = {
  create(format: DisplayFormat, config: RendererConfig): CardRenderer {
    switch (format) {
      case 'featured':  return new FeaturedRenderer(config);
      case 'manual':    return new CardsRenderer(config, true);
      case 'textHoriz': return new TextHorizRenderer(config);
      case 'textVert':  return new TextVertRenderer(config);
      case 'cards':
      default:          return new CardsRenderer(config, false);
    }
  },
};
